import { auth } from '../../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import axios from 'utils/axios';
import { v4 as uuidv4 } from 'uuid';

/**
 * Bulk-triggers weekly summary generation for an entire org — see
 * `trigger-recent-weekly-deal-summary-admin-api.md`.
 *
 * For every user in the org it takes their 10 most-recently-updated
 * (non-archived) deals, groups them into one shared weekly batch per user and
 * kicks off generation for each deal. That is what makes the multi-deal
 * reports (DEALS_CLOSING_THIS_WEEK, consolidated RISK_REPORT) show their real
 * behaviour — a single-deal trigger never produces a batch worth consolidating.
 *
 * Async: resolves once every trigger call has been made, NOT once the reports
 * are written. Window is always LAST_WEEK and the daily dedupe lock is skipped,
 * so re-running for the same org on the same day re-triggers generation.
 *
 * Note: this endpoint lives on the user-service (`/us`), not the admin-service,
 * and authenticates the caller via the `x-api-token` header.
 */
const triggerRecentWeeklyDealSummaries = (orgId, callback, errCb) => {
  return async (dispatch) => {
    dispatch({ type: 'TRIGGERING_RECENT_WEEKLY_DEAL_SUMMARIES' });
    try {
      let unsubscribe = onAuthStateChanged(auth, async (user) => {
        const token = await user?.getIdToken();
        const reqId = uuidv4();
        try {
          const response = await axios.post(
            `/ads/weekly-deal-summary/trigger-recent`,
            null,
            {
              params: {
                'request-id': reqId,
                sessionId: reqId,
                orgId: orgId,
              },
              headers: {
                Authorization: `Bearer ${token}`,
                'x-api-token': token,
              },
            },
          );
          if (response.status === 200) {
            await dispatch({
              type: 'TRIGGERING_RECENT_WEEKLY_DEAL_SUMMARIES_DONE',
            });
            if (typeof callback === 'function') {
              callback(response?.data);
            }
          } else {
            dispatch({
              type: 'TRIGGERING_RECENT_WEEKLY_DEAL_SUMMARIES_FAILED',
              payload: `Failed to trigger weekly deal summaries for recent deals`,
            });
            if (typeof errCb === 'function') {
              errCb(response?.data);
            }
          }
        } catch (e) {
          dispatch({
            type: 'TRIGGERING_RECENT_WEEKLY_DEAL_SUMMARIES_FAILED',
            payload: `Failed to trigger weekly deal summaries for recent deals`,
          });
          if (typeof errCb === 'function') {
            errCb(e?.response?.data || e?.message);
          }
        }
        unsubscribe();
      });
    } catch (e) {
      console.log(e);
      dispatch({
        type: 'TRIGGERING_RECENT_WEEKLY_DEAL_SUMMARIES_FAILED',
        payload: `Failed to trigger weekly deal summaries for recent deals`,
      });
      if (typeof errCb === 'function') {
        errCb(e?.message);
      }
    }
  };
};

export default triggerRecentWeeklyDealSummaries;
